import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { badgesAPI, centersAPI } from '../utils/api';
import AccessibleModal from '../components/common/AccessibleModal';

const CenterModal = ({ center, onClose, onSave, t, readOnly = false }) => {
  const generateRandomString = (length) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let random = '';

    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      const bytes = new Uint8Array(length);
      window.crypto.getRandomValues(bytes);
      random = Array.from(bytes, (b) => chars[b % chars.length]).join('');
    } else {
      random = Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    }

    return random;
  };

  const generateKioskUsername = () => {
    const base = (form.name || center?.name || 'centre')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24) || 'centre';

    return `${base}-kiosk`;
  };

  const generateKioskPassword = () => `Kio_${generateRandomString(16)}`;

  const generateApiKey = () => {
    return `cdj_${generateRandomString(40)}`;
  };

  const [form, setForm] = useState({
    name: center?.name || '',
    nameEn: center?.name_en || '',
    address: center?.address || '',
    streetNumber: center?.street_number || '',
    streetName: center?.street_name || '',
    apartment: center?.apartment || '',
    city: center?.city || '',
    province: center?.province || '',
    postalCode: center?.postal_code || '',
    phone: center?.phone || '',
    email: center?.email || '',
    logoUrl: center?.logo_url || '',
    badgeScanningEnabled: center?.badge_scanning_enabled !== false,
    kioskUsername: center?.badge_kiosk_username || '',
    kioskPassword: '',
    badgeApiKey: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const trimmedKioskUsername = form.kioskUsername.trim();
      const trimmedKioskPassword = form.kioskPassword.trim();
      const trimmedBadgeApiKey = form.badgeApiKey.trim();
      const hasExistingKioskCreds = !!center?.has_badge_kiosk_credentials;

      if ((trimmedKioskUsername && !trimmedKioskPassword && !hasExistingKioskCreds)
        || (!trimmedKioskUsername && trimmedKioskPassword)) {
        throw new Error('Renseignez l’identifiant et le mot de passe du kiosque ensemble (min. 8 caractères)');
      }

      const centerPayload = {
        name: form.name,
        nameEn: form.nameEn,
        address: form.address,
        streetNumber: form.streetNumber,
        streetName: form.streetName,
        apartment: form.apartment,
        city: form.city,
        province: form.province,
        postalCode: form.postalCode,
        phone: form.phone,
        email: form.email,
        logoUrl: form.logoUrl || null,
        badgeScanningEnabled: !!form.badgeScanningEnabled,
      };

      let centerId = center?.id || null;
      if (centerId) {
        await centersAPI.update(centerId, centerPayload);
      } else {
        const created = await centersAPI.create(centerPayload);
        centerId = created?.data?.id || null;
      }

      if (centerId) {
        const badgeConfigPayload = {};
        const shouldPersistBadgeConfig = Boolean(
          center
          || trimmedKioskUsername
          || trimmedKioskPassword
          || trimmedBadgeApiKey
        );

        if (shouldPersistBadgeConfig) {
          badgeConfigPayload.badgeScanningEnabled = !!form.badgeScanningEnabled;
          badgeConfigPayload.logoUrl = form.logoUrl || null;
          if (trimmedKioskUsername) badgeConfigPayload.kioskUsername = trimmedKioskUsername;
          if (trimmedKioskPassword) badgeConfigPayload.kioskPassword = trimmedKioskPassword;
          if (trimmedBadgeApiKey) badgeConfigPayload.badgeApiKey = trimmedBadgeApiKey;
          await badgesAPI.updateCenterConfig(centerId, badgeConfigPayload);
        }
      }

      setSuccess('Configuration centre sauvegardée');
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    }
    finally { setLoading(false); }
  };

  return (
    <AccessibleModal
      title={readOnly ? 'Consulter centre' : center ? t('centers.editCenter') : t('centers.addCenter')}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{readOnly ? t('common.close') : t('common.cancel')}</button>
          {!readOnly && (
            <button type="submit" form="center-form" className="btn btn-primary" disabled={loading}>{loading ? t('common.loading') : t('common.save')}</button>
          )}
        </>
      )}
    >
        <form id="center-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-error" role="alert">⚠ {error}</div>}
            {success && <div className="alert alert-success" role="status">{success}</div>}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('centers.name')} (FR) *</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required disabled={readOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('centers.name')} (EN)</label>
                <input className="form-input" value={form.nameEn} onChange={e => setForm(f => ({ ...f, nameEn: e.target.value }))} disabled={readOnly} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">No. rue</label>
                <input className="form-input" value={form.streetNumber} onChange={e => setForm(f => ({ ...f, streetNumber: e.target.value }))} disabled={readOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">Nom de la rue</label>
                <input className="form-input" value={form.streetName} onChange={e => setForm(f => ({ ...f, streetName: e.target.value }))} disabled={readOnly} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Appartement</label>
                <input className="form-input" value={form.apartment} onChange={e => setForm(f => ({ ...f, apartment: e.target.value }))} disabled={readOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">Ville</label>
                <input className="form-input" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} disabled={readOnly} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Province</label>
                <input className="form-input" value={form.province} onChange={e => setForm(f => ({ ...f, province: e.target.value }))} disabled={readOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">Code postal</label>
                <input className="form-input" value={form.postalCode} onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))} disabled={readOnly} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('centers.phone')}</label>
                <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} disabled={readOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('centers.email')}</label>
                <input type="email" className="form-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} disabled={readOnly} />
              </div>
            </div>

            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--gray-200)' }}>
              <h4 style={{ margin: 0, marginBottom: 10 }}>Configuration du badgage</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className={`badge ${form.badgeScanningEnabled ? 'badge-success' : 'badge-danger'}`}>
                  {form.badgeScanningEnabled ? 'Badgage actif' : 'Badgage désactivé'}
                </span>
                {center?.has_badge_kiosk_credentials && <span className="badge badge-info">Identifiants du kiosque configurés</span>}
                {center?.has_badge_api_key && <span className="badge badge-primary">Jeton API configuré</span>}
              </div>

              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!form.badgeScanningEnabled}
                    onChange={(e) => setForm(f => ({ ...f, badgeScanningEnabled: e.target.checked }))}
                    disabled={readOnly}
                  />
                  Autoriser le badgage en temps réel
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Logo badge (URL)</label>
                  <input
                    className="form-input"
                    value={form.logoUrl}
                    onChange={e => setForm(f => ({ ...f, logoUrl: e.target.value }))}
                    placeholder="https://..."
                    disabled={readOnly}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Identifiant du kiosque du centre</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="form-input"
                      value={form.kioskUsername}
                      onChange={e => setForm(f => ({ ...f, kioskUsername: e.target.value.toLowerCase() }))}
                      placeholder="ex: centre1-kiosk"
                      disabled={readOnly}
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => setForm((f) => ({ ...f, kioskUsername: generateKioskUsername() }))}
                      >
                        Générer
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Mot de passe du kiosque (laisser vide pour conserver)</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="password"
                      className="form-input"
                      value={form.kioskPassword}
                      onChange={e => setForm(f => ({ ...f, kioskPassword: e.target.value }))}
                      placeholder={readOnly ? '********' : 'Nouveau mot de passe'}
                      disabled={readOnly}
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => setForm((f) => ({ ...f, kioskPassword: generateKioskPassword() }))}
                      >
                        Générer
                      </button>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Jeton API badge (laisser vide pour conserver)</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="form-input"
                      value={form.badgeApiKey}
                      onChange={e => setForm(f => ({ ...f, badgeApiKey: e.target.value }))}
                      placeholder={readOnly ? '********' : 'Nouveau jeton API'}
                      disabled={readOnly}
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => setForm(f => ({ ...f, badgeApiKey: generateApiKey() }))}
                      >
                        Générer
                      </button>
                    )}
                  </div>
                  {!readOnly && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--gray-500)' }}>
                      Le token généré respecte la validation API (12+ caractères).
                    </div>
                  )}
                </div>
              </div>
            </div>
        </form>
    </AccessibleModal>
  );
};

const CentersPage = () => {
  const { t } = useTranslation();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalCenter, setModalCenter] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [openMenuCenterId, setOpenMenuCenterId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await centersAPI.getAll();
      setCenters(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const closeMenu = () => setOpenMenuCenterId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    await centersAPI.delete(id);
    load();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('centers.title')}</h2>
          <p className="text-muted">{centers.length} centres</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setModalCenter(null); setModalReadOnly(false); setShowModal(true); }}>+ {t('centers.addCenter')}</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
        {loading ? (
          <div style={{ padding: 48 }}><div className="spinner" /></div>
        ) : centers.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--gray-400)' }}>
            {t('centers.noCenters')}
          </div>
        ) : centers.map(c => (
          <div key={c.id} className="card" style={{ padding: 24 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, background: 'var(--primary-50)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🏢</div>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuCenterId(curr => (curr === c.id ? null : c.id));
                  }}
                  aria-label="Ouvrir le menu d'actions"
                  aria-haspopup="menu"
                  aria-expanded={openMenuCenterId === c.id}
                >
                  ⋮
                </button>
                {openMenuCenterId === c.id && (
                  <div
                    role="menu"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '110%',
                      right: 0,
                      minWidth: 160,
                      background: 'white',
                      border: '1px solid var(--gray-200)',
                      borderRadius: 8,
                      boxShadow: 'var(--shadow-md)',
                      zIndex: 20,
                      padding: 6,
                    }}
                  >
                    <button
                      className="btn btn-sm btn-outline"
                      style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                      onClick={() => {
                        setModalCenter(c);
                        setModalReadOnly(true);
                        setShowModal(true);
                        setOpenMenuCenterId(null);
                      }}
                    >
                      Consulter
                    </button>
                    <button
                      className="btn btn-sm btn-outline"
                      style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                      onClick={() => {
                        setModalCenter(c);
                        setModalReadOnly(false);
                        setShowModal(true);
                        setOpenMenuCenterId(null);
                      }}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      style={{ width: '100%', justifyContent: 'flex-start' }}
                      onClick={() => {
                        setOpenMenuCenterId(null);
                        handleDelete(c.id);
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <h3 style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{c.name}</h3>
            {c.name_en && <div style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 12 }}>{c.name_en}</div>}
            {c.address && <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 4 }}>📍 {c.address}</div>}
            {c.phone && <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 4 }}>📞 {c.phone}</div>}
            {c.email && <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 4 }}>✉ {c.email}</div>}
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span className={`badge ${c.badge_scanning_enabled ? 'badge-success' : 'badge-danger'}`}>
                {c.badge_scanning_enabled ? 'Badgage actif' : 'Badgage désactivé'}
              </span>
              {c.has_badge_kiosk_credentials && <span className="badge badge-info">Kiosque OK</span>}
              {c.has_badge_api_key && <span className="badge badge-primary">Jeton API OK</span>}
            </div>
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 22 }}>👥</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 20 }}>{c.staff_count || 0}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('centers.staffCount')}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <CenterModal
          center={modalCenter}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }}
          t={t}
          readOnly={modalReadOnly}
        />
      )}
    </div>
  );
};

export default CentersPage;
